/**
 * Classification and copy for a failed sign-in provisioning call
 * (`GET /auth/user`, made by ClerkSync in App.jsx).
 *
 * The backend answers 503 with `error_code: "DATABASE_UNAVAILABLE"` when the
 * Clerk token verified but the database didn't answer (utils/clerk_auth.py).
 * That used to come back as a 401 and render "Authentication Setup Required"
 * with a hint to fix the Clerk Dashboard — which is how a stale DATABASE_URL
 * password sent a debugging session to Clerk. The user can't fix an outage, so
 * it gets its own, honest screen.
 */
export const DATABASE_UNAVAILABLE_CODE = 'DATABASE_UNAVAILABLE';

export const PROVISION_ERROR_KIND = Object.freeze({
  SETUP: 'setup',
  UNAVAILABLE: 'unavailable',
});

const COPY = {
  [PROVISION_ERROR_KIND.SETUP]: {
    title: 'Authentication Setup Required',
    hint: 'After saving the change in Clerk Dashboard, refresh this page to continue.',
  },
  [PROVISION_ERROR_KIND.UNAVAILABLE]: {
    title: 'Service Temporarily Unavailable',
    hint: 'Please try again in a few minutes.',
  },
};

export const SERVICE_UNAVAILABLE_MESSAGE =
  "You're signed in, but Hestia can't load your account right now because of a problem on our end.";

/**
 * Decide what ClerkSync does with a failed provisioning call.
 *
 * @param {Object} err - axios error from AuthService.getCurrentUser()
 * @param {boolean} canRetry - whether a retry is still within budget
 * @returns {{ retry: boolean, transient: boolean, error: ({kind: string, message: string}|null) }}
 *   `retry` — schedule another attempt; `transient` — allow a fresh attempt on
 *   the next auth event; `error` — the screen to show, or null to stay silent.
 */
export function classifyProvisionFailure(err, canRetry) {
  const status = err?.response?.status;
  const data = err?.response?.data;

  if (data?.code === 'missing_email_claim') {
    // Permanent config error — never retry.
    return {
      retry: false,
      transient: false,
      error: { kind: PROVISION_ERROR_KIND.SETUP, message: data.message },
    };
  }

  const transient = !status || status >= 500 || status === 401;
  if (transient && canRetry) {
    return { retry: true, transient, error: null };
  }

  if (status === 503 && data?.error_code === DATABASE_UNAVAILABLE_CODE) {
    return {
      retry: false,
      transient,
      error: { kind: PROVISION_ERROR_KIND.UNAVAILABLE, message: SERVICE_UNAVAILABLE_MESSAGE },
    };
  }

  if (!status || status >= 500) {
    // Other 5xx / network: stay silent, the next auth event re-tries.
    return { retry: false, transient, error: null };
  }

  return {
    retry: false,
    transient,
    error: { kind: PROVISION_ERROR_KIND.SETUP, message: err?.message || 'Authentication error' },
  };
}

/**
 * Title and follow-up hint for a provision error screen.
 * @param {{kind: string}} provisionError
 * @returns {{ title: string, hint: string }}
 */
export function provisionErrorCopy(provisionError) {
  return COPY[provisionError?.kind] || COPY[PROVISION_ERROR_KIND.SETUP];
}
